// src/api/products.ts
import { apiClient } from './client';
import type { Product, ApiResponse, ProductVariant, PaginatedResponse } from '../types';

export const productsAPI = {
  // Get all products (catalog view)
  async getProducts(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    category?: string;
    in_stock_only?: boolean;
  }): Promise<ApiResponse<PaginatedResponse<Product[]>>> {
    try {
      const response = await apiClient.get<PaginatedResponse<Product[]>>('/store/products/', {
        params: {
          ...params,
          is_active: true,
        }
      });

      // Make sure response.data is the paginated object
      return {
        data: {
          results: response.data.results || response.data,
          count: response.data.count || (Array.isArray(response.data) ? response.data.length : 0),
          next: response.data.next,
          previous: response.data.previous
        },
        status: response.status
      };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch products',
        status: error.response?.status || 500,
      };
    }
  },
  // Get single product with variants
  async getProduct(id: number): Promise<ApiResponse<Product>> {
    try {
      const response = await apiClient.get<Product>(`/store/products/${id}/`);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch product',
        status: error.response?.status || 500,
      };
    }
  },

  // Get product variants
  async getProductVariants(productId: number): Promise<ApiResponse<ProductVariant[]>> {
    try {
      const response = await apiClient.get<ProductVariant[]>(
        `/store/variants/?product=${productId}`
      );
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch variants',
        status: error.response?.status || 500,
      };
    }
  },

  // Check stock availability
  async checkStock(productVariantId: number, quantity: number): Promise<ApiResponse<boolean>> {
    try {
      const response = await apiClient.get<{ available: boolean }>(
        `/store/stock/check/?variant=${productVariantId}&quantity=${quantity}`
      );
      return {
        data: response.data.available,
        status: response.status
      };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to check stock',
        status: error.response?.status || 500,
      };
    }
  },

  // Get available stock quantities
  async getAvailableStock(variantIds: string): Promise<ApiResponse<Record<string, number>>> {
    try {
      const response = await apiClient.get<Record<string, number>>(
        `/store/products/available_stock/?variant_ids=${variantIds}`
      );
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error: any) {
      return {
        error: error.response?.data?.error || 'Failed to fetch stock availability',
        status: error.response?.status || 500,
      };
    }
  },

  // Get low stock products
  async getLowStock(threshold: number = 5): Promise<ApiResponse<Product[]>> {
    try {
      const response = await apiClient.get<Product[]>('/store/products/', {
        params: { low_stock: true, threshold },
      });
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch low stock products',
        status: error.response?.status || 500,
      };
    }
  },

  async deleteProduct(productId: number): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.delete(`/vendors/products/${productId}/delete/`, {
        data: { product_id: productId }
      });
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to delete product',
        status: error.response?.status || 500
      };
    }
  },

  // Vendor product endpoints
  createProduct: (data: any) => {
    const isFormData = data instanceof FormData;
    return apiClient.post('/vendors/products/create/', data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
    });
  },

  updateProduct: (id: number, data: any) => {
    const isFormData = data instanceof FormData;
    return apiClient.put(`/vendors/products/${id}/update/`, data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
    });
  },

  // Toggle product active status
  vendorToggleProductActive: (id: number, isActive: boolean) => {
    return apiClient.patch(`/vendors/products/${id}/toggle-active/`, { is_active: isActive });
  },

  // Toggle variant active status
  vendorToggleVariantActive: (productId: number, variantId: number, isActive: boolean) => {
    return apiClient.patch(`/vendors/products/${productId}/variants/${variantId}/toggle-active/`, { is_active: isActive });
  },

  getVendorProduct: (id: number) => {
    return apiClient.get(`/vendors/products/${id}/`);
  },

  getVendorProducts: async (params?: {
    status?: string;
    page?: number;
    page_size?: number;
  }) => {
    try {
      const response = await apiClient.get('/vendors/products/', { params });
      return { data: response.data, error: null };
    } catch (error: any) {
      return {
        data: null,
        error: error.response?.data?.detail || 'Failed to fetch products'
      };
    }
  },

  resubmitProduct: (id: number) => {
    return apiClient.post(`/vendors/products/${id}/resubmit/`);
  },

  // Category Attributes (for spec recommendations)
  getCategoryAttributes: async (categories: string) => {
    const response = await apiClient.get(`/store/category-attributes/recommendations/?categories=${encodeURIComponent(categories)}`);
    return response.data;
  },

  // Archive a product
  archiveProduct: (id: number) => {
    return apiClient.post(`/vendors/products/${id}/archive/`);
  },

  // Unarchive a product
  unarchiveProduct: (id: number) => {
    return apiClient.post(`/vendors/products/${id}/unarchive/`);
  },

  // Rollback changes to the last approved version
  rollbackChanges: (id: number) => {
    return apiClient.post(`/vendors/products/${id}/rollback-changes/`);
  },
};